/* eslint-disable no-param-reassign */
/* eslint-disable object-curly-newline */
/* eslint-disable no-tabs */
/* eslint-disable react/display-name */
import { isEmpty } from 'lodash';
import { LiqPrefLedgerConfig, LiqPrefLedgerItem, SecurityModel } from 'api';
import * as CAPTABLE from 'common/constants/cap-table';
import { PERCENTAGE, STRING } from 'common/constants/gridType';
import * as inputOptions from 'common/constants/inputs';
import * as validationMessages from 'common/constants/messages/validations';
import {
  COMMON_STOCK as COMMON,
  CONVERTIBLE_NOTES as CONVERTIBLE,
  LIST,
  OPTION,
  PREFERRED_STOCK as PREFERRED,
  UNISSUED_OPTIONS as UNISSUED,
  WARRANT,
} from 'common/constants/securityTypes';
import { capTableTwoDecimalPercentFormat } from 'common/formats/formats';
import { TableData } from 'common/types/scalarSpreadsheet';
import { SelectValueViewer } from 'components';
import { GridDate, GridDateEditor, GridDateValueViewer, GridSelect } from 'components/FeaturedSpreadsheet/components';
import { GridDialogValueViewer } from 'components/Grid';
import VariableValueViewer from 'components/ScalarSpreadsheet/utilities/variable-value-viewer/VariableValueViewer';
import { CellValue, ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { seniorityOptions } from 'pages/CapTable/cap-table/utilities/getOptionsForSelect';
import applyPreFxAnnotation from 'pages/CapTable/fund-ownership/components/shares-ledger/utilities/applyPreFxAnnotation';
import { useReviewStore } from 'store/useReviewStore';
import { addClassName, removeClassName, removeValidationClasses, toString } from 'utilities';
import { sameAiValue } from './aiNumericValue';
import { hasOwnPreferredTerms, isCapTableFieldApplicable } from './fieldApplicability';
import { matchAiOption, normalizeAiSelectValue } from './resolveAiOptionValue';
import { AI_SUGGESTED_CLASS, reviewBaseOf } from './reviewComposition';
import { syncReferenceGreen } from './securityAiPatches';
import {
  getOptions,
  getSecurityType,
  isMissingUnderlyingSecurityValue,
  isNoneAsCommonValue,
  NONE_AS_COMMON_OPTION,
  NONE_AS_COMMON_VALUE,
} from './underlyingSecurities';
import {
  ConvertibleNote,
  DividendRateLedger,
  LiquidationPriority,
  MultipleInvestmentDates,
  OptionsLedger,
} from '../components';
import { VARIABLE_VALUE } from './variablePropertiesUtils';
import { AIFindingValueViewer } from '../components/ai-security-finding-popover';
import AIMergeValueViewer from '../components/ai-security-finding-popover/AIMergeValueViewer';
import {
  CapTableCell,
  CapTableCellWithReferences,
  CaptableColumn,
} from '../components/captable-table/CaptableTable.props';
import CitationValueViewer from '../components/citation-value-viewer/CitationValueViewer';
import { ConvertibleNotesLedgerCell } from '../components/convertible-notes-ledger/types';
import LiquidationPreferenceLedgerDialog from '../components/liquidation-preference-ledger/LiquidationPreferenceLedger';
import { MultipleInvestmentDatesCell } from '../components/multiple-investments/types';
import { OptionsLedgerCell } from '../components/options-ledger/types';

interface CapTableEnhancedModel extends CapTableCell {
  securityRef: string;
  dividend_rate_ledger_items: any[];
}

const conditions = ({
  cell,
  cells,
  sheet,
  tableData,
}: {
  cell: CapTableCellWithReferences;
  cells: any;
  // The owning SpreadsheetConfig. `conditions` runs on the pre-render cell map,
  // so to repaint a cell after an out-of-band mutation we reach the live
  // rendered Cell instance via `sheet.cells[key]` (see the OIP Pre-FX preview).
  sheet?: { cells?: Record<string, CapTableCellWithReferences> };
  tableData: TableData<CapTableEnhancedModel> & {
    securities: SecurityModel[];
    has_dividend_rate_ledger: boolean;
    has_liquidation_preference_ledger: boolean;
    captableCurrency?: string;
    measurementDate?: string;
  };
}) => {
  const { captable: capCells } = cells as { captable: Record<string, CapTableCellWithReferences> };
  const { alias, columnLegend, className, value, columnId, defaultValue, tooltipMessages = [] } = cell;
  const underlyingSecurityCell = capCells[columnLegend! + CAPTABLE.UNDERLYING_SECURITY_ROW_NUMBER];
  const hasDividendsCell = capCells[columnLegend! + CAPTABLE.CUMULATIVE_DIVIDENDS_ROW_NUMBER] || '';
  const hasDividends = Number(hasDividendsCell.value) === inputOptions.YES;
  const preferredTerms = hasOwnPreferredTerms(cell, capCells);
  const hasMultipleInvestmentsCell = capCells[columnLegend! + CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER] || '';
  const hasMultipleInvestments = Boolean(Number(hasMultipleInvestmentsCell.value));
  const hasDivRateLedgerItems = Boolean(
    tableData.columns.find(({ security_ref }) => cell.columnRef === security_ref)?.dividend_rate_ledger_items?.length
  );
  const { isDisabled } = tableData;
  const getValue = () => (defaultValue && !Number.isNaN(defaultValue) ? Number(defaultValue) : (defaultValue ?? ''));

  const resetCellConfig = () => {
    cell.hidden = true;
    cell.readOnly = true;
    cell.forceComponent = false;
    cell.disableEvents = false;
    cell.component = null;
    cell.dialog = null;
    cell.greaterThan = null;
    cell.isDisabled = isDisabled;
  };

  const setAsRequired = (isRequired = true) => {
    cell.isRequired = isRequired;
  };

  const setAsEditable = () => {
    resetCellConfig();
    cell.readOnly = isDisabled;
    cell.hidden = false;
  };

  const setAsReadOnly = () => {
    resetCellConfig();
    cell.readOnly = true;
    cell.hidden = false;
  };

  const setAsHidden = (keepValue = false) => {
    resetCellConfig();
    if (!keepValue) {
      cell.value = getValue() as string;
      cell.expr = cell.value;
    }
    cell.className = removeClassName(removeValidationClasses(className ?? ''), 'read-only--white');
    cell.tooltipMessages = null;
    cell.isRequired = false;
    cell.hidden = true;
  };

  const addTooltipMessage = (messageToAdd: string) => {
    if (!tooltipMessages?.includes(messageToAdd)) {
      tooltipMessages?.push(messageToAdd);
    }

    cell.tooltipMessages = tooltipMessages;
  };

  const removeTooltipMessage = (messageToDelete: string) => {
    if (tooltipMessages?.includes(messageToDelete)) {
      const index = tooltipMessages?.findIndex(message => message === messageToDelete);
      tooltipMessages?.splice(index, 1);
    }
  };

  const isSecurityType = (type: number) => {
    const securityType = capCells[cell.columnLegend! + CAPTABLE.SECURITY_TYPE_ROW_NUMBER];

    if (type && securityType?.value) {
      return securityType.value.toString() === type.toString();
    }
    return false;
  };

  const isUnderlyingSecurity = (type: number) => {
    const securityType = getSecurityType(underlyingSecurityCell.value as string, capCells);

    if (type === COMMON && isSecurityType(OPTION)) {
      const hasCommonSecurity = getOptions({ securityType: OPTION, cells: capCells }).length > 0;
      const persistedWithoutUnderlying =
        underlyingSecurityCell.columnId !== 0 && isMissingUnderlyingSecurityValue(underlyingSecurityCell.value);

      if (!hasCommonSecurity && (isNoneAsCommonValue(underlyingSecurityCell.value) || persistedWithoutUnderlying)) {
        return true;
      }
    }

    if (securityType && type) {
      return securityType.toString() === type.toString();
    }
    return false;
  };

  const enableMultipleInvestments = () => {
    setAsReadOnly();
    cell.minValue = 1;
    cell.dialog = {
      content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
    };
    cell.disableEvents = true;
  };

  if (cell.exprBuilderEnabled && cell.expr === '=') {
    cell.minValue = null;
    cell.tooltipMessages = ['Cell in reference state'];
    return cell;
  }
  if (cell.exprBuilderEnabled && cell.expr !== '=') {
    cell.tooltipMessages = null;
  }

  // 1. Security Name
  if (alias === CAPTABLE.SECURITY_NAME_ALIAS) {
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      // preferred doesn't have multiple strike price -> no ledger no virtual column
      if (isUnderlyingSecurity(COMMON) || isUnderlyingSecurity(PREFERRED)) {
        if (!columnId && columnId !== 0) {
          setAsReadOnly();
          addTooltipMessage('Title of a virtual column is read-only');
          cell.className = removeClassName(className ?? '', 'text-blue');
          cell.forceComponent = true;
          cell.disableEvents = true;
          cell.tooltipMessages = tooltipMessages;
        } else {
          setAsEditable();
        }
      }
    }

    // Reveal the official legal name on hover whenever it is set and differs
    // from the display alias (SCAL-9184). Drop any stale message first so the
    // tooltip tracks legal-name edits on conditions re-runs.
    const legalNameCell = capCells[columnLegend! + CAPTABLE.LEGAL_NAME_ROW_NUMBER];
    const legalName = toString(legalNameCell?.value ?? '');
    tooltipMessages.filter(message => message.startsWith('Legal name: ')).forEach(removeTooltipMessage);
    if (legalName && legalName !== toString(value ?? '')) {
      addTooltipMessage(`Legal name: ${legalName}`);
    }
    cell.tooltipMessages = tooltipMessages;
  }

  // 02. Legal Name
  if (alias === CAPTABLE.LEGAL_NAME_ALIAS) {
    // Mirror the Security Name handling for warrant/option virtual columns
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(COMMON) || isUnderlyingSecurity(PREFERRED)) {
        if (!columnId && columnId !== 0) {
          setAsReadOnly();
          addTooltipMessage('This cell is part of a virtual column');
          cell.forceComponent = true;
          cell.disableEvents = true;
          cell.tooltipMessages = tooltipMessages;
        } else {
          setAsEditable();
        }
      }
    }
  }

  // 03.  Investment Date
  if (alias === CAPTABLE.INVESTMENT_DATE_ALIAS) {
    if (isSecurityType(PREFERRED) || isSecurityType(CONVERTIBLE)) {
      if (hasMultipleInvestments) {
        enableMultipleInvestments();
      }

      if (!hasDividends || !hasMultipleInvestments) {
        setAsEditable();
        cell.gridDateComponent = GridDate as any;
        cell.dataEditor = (props: any) => <GridDateEditor {...props} />;
        cell.valueViewer = (props: any) => <GridDateValueViewer {...props} />;
        cell.isRequired = true;
      }

      cell.isRequired = true;
    } else {
      setAsHidden();
    }
  }

  // 04.  Security Type
  if (alias === CAPTABLE.SECURITY_TYPE_ALIAS) {
    cell.required = true;
  }

  // 05.  Original Issue Price
  if (alias === CAPTABLE.ISSUE_PRICE_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      // editable
      setAsEditable();
      setAsRequired(true);
      cell.greaterThan = 0;
      cell.skipInitialValidation = true;

      const securityColumn = (tableData.columns as unknown as CaptableColumn[]).find(
        ({ security_ref }) => cell.columnRef === security_ref
      );
      const { captableCurrency, measurementDate } = tableData;
      // The in-cell MoneyChip is the only Pre-FX affordance here (the hover
      // bar is feature-gated off and Pre-FX has no inline icon). withMoneyChip
      // renders that chip only when the host cell exposes a currency code via
      // resolveCurrencyCode(cell.currency). FOD/CFL money cells inherit it from
      // their LedgerTable's `currency` prop; the cap-table securities grid does
      // not pass one through, so set it explicitly or the annotation attaches
      // but renders indistinguishably from a plain cell.
      if (captableCurrency) {
        cell.currency = { code: captableCurrency, currencyCode: captableCurrency };
      }
      // Attach the Pre-FX annotation unconditionally, mirroring the FOD ledger:
      // it always attaches; an undefined currency or date only skips the live
      // FX preview (see applyPreFxAnnotation). The lone guard is
      // idempotency - conditions re-runs on persistent cells, and a second
      // wrap would nest CellAnnotationValueViewer inside itself (recursive
      // stack). Keyed on the `cellAnnotationCellKey` the util sets, same as
      // the citation / AI-finding guards below.
      if (!cell.cellAnnotationCellKey) {
        const annotated = applyPreFxAnnotation({
          cell: cell as ICell,
          parentType: 'security',
          parentId: securityColumn?.id,
          parentRef: cell.columnRef ?? securityColumn?.security_ref,
          fieldAlias: CAPTABLE.ISSUE_PRICE_ALIAS,
          defaultCurrency: captableCurrency,
          hostCurrency: captableCurrency,
          transactionDate: measurementDate,
          onHostPreview: (previewValue: number | null) => {
            const nextValue = previewValue == null ? '' : String(previewValue);
            // The closure `cell` is a detached pre-`generateData` object with no
            // dispatchEvent, so mutating it can never repaint. Reach the live
            // rendered Cell instance through the sheet and fire its dispatchEvent
            // bus (the same mechanism handleCellChanged uses) so the converted
            // host amount shows immediately, FOD-style, before save.
            const liveCell = sheet?.cells?.[cell.key as string] ?? cell;
            liveCell.value = nextValue;
            liveCell.expr = nextValue;
            liveCell.dispatchEvent?.();
          },
        });
        Object.assign(cell, annotated);
      }
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.readOnly = true;
      cell.hidden = true;
      setAsRequired(false);
    } else {
      // hidden
      setAsHidden();
      setAsRequired(false);
      cell.minValue = null;
    }
  }

  // 06.  Shares Outstanding
  if (alias === CAPTABLE.SHARES_OUTSTANDING_ALIAS) {
    if (isSecurityType(UNISSUED)) {
      setAsHidden();
      setAsRequired(false);
    } else if (isSecurityType(PREFERRED)) {
      // Show ledger if has multiple investment dates
      if (hasMultipleInvestments) {
        enableMultipleInvestments();
      }

      if (!hasDividends || !hasMultipleInvestments) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = 1;
        cell.dialog = null;
      }
    } else if (isSecurityType(COMMON)) {
      setAsEditable();
      setAsRequired(true);
      cell.minValue = 1;
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(COMMON)) {
        if (!columnId && columnId !== 0) {
          addTooltipMessage('This cell is part of a virtual column');
        }

        setAsReadOnly();
        cell.minValue = 1;
        cell.dialog = {
          content: (
            <OptionsLedger
              cell={cell as OptionsLedgerCell}
              closeDialog={() => {}}
              isOption={isSecurityType(OPTION)}
            />
          ),
        };
        cell.disableEvents = true;
        cell.tooltipMessages = tooltipMessages;
        cell.readOnly = isDisabled;
      }

      if (isUnderlyingSecurity(PREFERRED)) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = 1;
      }

      if (!isUnderlyingSecurity(PREFERRED) && !isUnderlyingSecurity(COMMON)) {
        setAsEditable();
        setAsRequired(true);
        cell.minValue = null;
      }
    } else {
      setAsHidden(true);
      setAsRequired(false);
    }
  }

  // 07.  Conversion Rate
  if (alias === CAPTABLE.CONVERSION_RATE_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      setAsEditable();
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
        cell.expr = 1;
        cell.value = 1;
      }
      if (!underlyingSecurityCell.value) {
        setAsHidden();
      }
    } else if (isSecurityType(COMMON)) {
      setAsHidden();
      cell.expr = 1;
      cell.value = 1;
    } else if (isSecurityType(CONVERTIBLE) && preferredTerms) {
      setAsReadOnly();
      cell.expr = 1;
      cell.value = 1;
    } else {
      setAsHidden();
    }
  }

  // 08.  PIK Shares as of today
  if (alias === CAPTABLE.PIK_SHARES_ALIAS) {
    if (preferredTerms) {
      if (hasDividends) {
        const dividendPaymentTypeKey = columnLegend! + CAPTABLE.DIVIDEND_PAYMENT_TYPE_ROW_NUMBER;
        const dividendPaymentType = capCells[dividendPaymentTypeKey];
        const isCash = Number(dividendPaymentType.value) === inputOptions.NO;
        const isPIK = Number(dividendPaymentType.value) === inputOptions.YES;

        if (dividendPaymentType) {
          if (isCash) {
            resetCellConfig();
            cell.hidden = true;
            cell.value = null;
          }

          if (isPIK) {
            setAsReadOnly();
          }
        }
      } else {
        setAsHidden(true);
      }
    } else {
      setAsHidden(true);
    }
  }

  // 09.  Shares Fully Diluted (as converted)
  if (alias === CAPTABLE.SHARES_FULLY_DILUTED_ALIAS) {
    const sharesOutstanding = capCells[cell.columnLegend! + CAPTABLE.SHARES_OUTSTANDING_ROW_NUMBER];

    if (isSecurityType(UNISSUED)) {
      setAsEditable();
      setAsRequired(true);
      cell.minValue = 1;
      cell.value = (cell.value as number) > 0 ? cell.value : '';
      cell.expr = cell.value as number;
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.minValue = null;
      cell.dialog = {
        content: (
          <ConvertibleNote cell={cell as ConvertibleNotesLedgerCell} closeDialog={() => {}} isDisabled={isDisabled} />
        ),
        maxWidth: 'lg',
      };
      cell.disableEvents = true;
      cell.tooltipMessages = tooltipMessages;
      cell.readOnly = isDisabled;
    } else {
      cell.minValue = 0;
      cell.value = sharesOutstanding?.value === '' ? '' : cell.value;
      cell.dialog = null;
      setAsReadOnly();
      setAsRequired(false);
    }
  }

  // 10. Current Ownership
  if (alias === CAPTABLE.CURRENT_OWNERSHIP_ALIAS) {
    if (isSecurityType(UNISSUED)) {
      setAsReadOnly();
      cell.value = 0;
    } else {
      setAsReadOnly();
    }
  }

  // 12. Strike Price
  if (alias === CAPTABLE.STRIKE_PRICE_ALIAS) {
    // Hide cells when undelying is empty
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsEditable();
        cell.greaterThan = 0;
        cell.minValue = undefined;
        setAsRequired();
      }
      if (isUnderlyingSecurity(COMMON)) {
        if (!columnId && columnId !== 0) {
          addTooltipMessage('This cell is part of a virtual column');
        }

        setAsReadOnly();

        cell.forceComponent = true;
        cell.disableEvents = true;
        cell.dialog = {
          content: (
            <OptionsLedger
              cell={cell as OptionsLedgerCell}
              closeDialog={() => {}}
              isOption={isSecurityType(OPTION)}
            />
          ),
        };
        cell.greaterThan = isSecurityType(OPTION) ? null : 0;
        cell.minValue = isSecurityType(OPTION) ? 0 : undefined;
        cell.readOnly = isDisabled;
        setAsRequired();
      }
      if (!isUnderlyingSecurity(PREFERRED) && !isUnderlyingSecurity(COMMON)) {
        // Keep an Option's ledger aggregate while its underlying selection is
        // temporarily empty (for example, when Common is added and the
        // None-as-Common fallback is removed). The required underlying field
        // still blocks saving until the user selects the real Common security.
        setAsHidden(isSecurityType(OPTION));
        cell.greaterThan = null;
        cell.minValue = undefined;
        setAsRequired(false);
      }
    } else {
      setAsHidden();
      cell.greaterThan = null;
    }
  }

  // 13.  Underlying Security
  if (alias === CAPTABLE.UNDERLYING_SECURITY_ALIAS) {
    if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      const underlyingSecurities = getOptions({
        securityType: isSecurityType(WARRANT) ? WARRANT : OPTION,
        cells: capCells,
      });
      const canUseNoneAsCommon = isSecurityType(OPTION) && isEmpty(underlyingSecurities);

      if (isEmpty(underlyingSecurities) && !canUseNoneAsCommon) {
        const preferredOrCommonMsg = `There are no "${LIST[PREFERRED - 1]}" or "${LIST[COMMON - 1]}" securities`;
        const commonMsg = `There are no "${LIST[COMMON - 1]}" securities`;

        // Clean previous tooltips
        removeTooltipMessage(preferredOrCommonMsg);
        removeTooltipMessage(commonMsg);

        const message = isSecurityType(WARRANT) ? preferredOrCommonMsg : commonMsg;
        addTooltipMessage(message);

        cell.value = '0';
        cell.hidden = false;
        cell.readOnly = isDisabled;
        cell.forceComponent = true;
        cell.options = [];
        cell.className = addClassName(className ?? '', 'warning');
      } else {
        const selectableSecurities = canUseNoneAsCommon ? [NONE_AS_COMMON_OPTION] : underlyingSecurities;
        const persistedWithoutUnderlying = columnId !== 0 && isMissingUnderlyingSecurityValue(value);
        const effectiveValue = canUseNoneAsCommon && persistedWithoutUnderlying ? NONE_AS_COMMON_VALUE : value;
        // Verify if the Underlying Security selected is available
        const isListed = selectableSecurities.find(
          security => toString(security.id) === toString(effectiveValue as number)
        );

        cell.value = !isListed ? null : effectiveValue;
        cell.hidden = false;
        cell.readOnly = isDisabled;
        cell.forceComponent = true;
        cell.tooltipMessages = [];
        cell.options = selectableSecurities as unknown as SecurityModel[];
        cell.className = removeClassName(className ?? '', 'warning');
      }
    } else {
      cell.hidden = true;
      cell.readOnly = true;
      cell.forceComponent = false;
      cell.tooltipMessages = [];
      cell.className = removeClassName(className ?? '', 'warning');
    }
  }

  // 15.  Liquidation Preference
  if (alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS) {
    if (preferredTerms) {
      setAsEditable();
      cell.minValue = 0;
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
      }
    } else {
      setAsHidden();
      cell.minValue = 0;
    }
  }

  // 16.  Seniority
  if (cell.alias === CAPTABLE.SENIORITY_ALIAS) {
    if (preferredTerms) {
      cell.dataEditor = (props: any) => <LiquidationPriority options={seniorityOptions(props)} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
      cell.readOnly = isDisabled;
      cell.hidden = false;
      setAsRequired();
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsReadOnly();
      } else if (isUnderlyingSecurity(COMMON)) {
        setAsHidden();
      }
    } else {
      setAsHidden();
      setAsRequired(false);
      cell.minValue = 0;
    }
  }

  // 17.  Participating
  if (alias === CAPTABLE.PARTICIPATION_ALIAS) {
    if (preferredTerms) {
      setAsRequired();
      cell.hidden = false;
      cell.readOnly = isDisabled;
      cell.forceComponent = true;
      cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
    } else if (isSecurityType(WARRANT) || isSecurityType(OPTION)) {
      if (isUnderlyingSecurity(PREFERRED)) {
        setAsRequired(false);
        cell.readOnly = true;
      }
      setAsHidden();
    } else {
      setAsHidden();
    }
  }

  // 18.  Participation Cap
  if (alias === CAPTABLE.IS_PARTICIPATION_CAP_ALIAS) {
    if (isCapTableFieldApplicable(cell, capCells, 'evidence')) {
      setAsRequired();
      cell.hidden = false;
      cell.readOnly = isDisabled;
      cell.forceComponent = true;
      cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
    } else {
      setAsHidden();
    }
  }

  // 19.  Participation Cap (X)
  if (alias === CAPTABLE.PARTICIPATION_CAP_ALIAS) {
    if (isCapTableFieldApplicable(cell, capCells, 'evidence')) {
      setAsEditable();
    } else {
      setAsHidden();
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 20.  Cumulative Dividends
  if (alias === CAPTABLE.CUMULATIVE_DIVIDENDS_ALIAS) {
    if (preferredTerms) {
      cell.hidden = false;
      cell.readOnly = isDisabled;
      cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
      cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
      setAsRequired();
    } else {
      setAsHidden();
    }
  }

  // 21.  Multiple Investment Dates?
  if (alias === CAPTABLE.HAS_MULTIPLE_INVESTMENT_DATES_ALIAS) {
    if (isSecurityType(PREFERRED)) {
      const multipleInvestmentsKey = columnLegend! + CAPTABLE.MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER;
      const hasMultipleInvestments = Boolean(Number(cell.value));
      const { securities } = tableData;

      const securityWithMultipleInvestments = ((Array.isArray(securities) &&
        securities.find(security => security.order === cell.columnOrder)) ||
        {}) as SecurityModel;
      const multipleInvestments = isEmpty(capCells[multipleInvestmentsKey].value)
        ? securityWithMultipleInvestments.multiple_investments
        : capCells[multipleInvestmentsKey].value;

      // Toggle tooltip and warning based on the presence of multiple investments
      if (hasDividends && hasMultipleInvestments && (isEmpty(multipleInvestments) || multipleInvestments.length < 2)) {
        cell.className = addClassName(cell.className ?? '', 'warning');
        addTooltipMessage(validationMessages.MIN_INVESTMENTS);
      }
      if (!hasDividends || !hasMultipleInvestments || multipleInvestments?.length >= 2) {
        removeTooltipMessage(validationMessages.MIN_INVESTMENTS);
        cell.className = removeClassName(cell.className ?? '', 'warning');
      }

      // tooltip for liquidation preference ledger in case of multiple investments
      const liqPrefTooltip = hasMultipleInvestments
        ? ['Multi-date support pending: Liquidation preference for complex schedules must be calculated manually.']
        : null;
      Object.values(capCells)
        .filter(cell => cell.alias.includes('fund_liquidation_preference'))
        .forEach(liqPrefCell => {
          liqPrefCell.tooltipMessages = liqPrefTooltip;
        });

      if (hasDividends) {
        cell.readOnly = isDisabled;
        cell.hidden = false;
        cell.dialog = {
          content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
        };
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
        cell.valueViewer = (props: any) => (
          <GridDialogValueViewer isDisabled={isDisabled} options={inputOptions.YES_NO} {...props} />
        );
        setAsRequired();
        cell.disableEvents = true;
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
        cell.tooltipMessages = tooltipMessages;
        cell.expr = defaultValue as number;
        cell.value = defaultValue as number;
      }
    } else if (isSecurityType(CONVERTIBLE)) {
      cell.value = getValue() as CellValue;
      cell.dialog = {
        content: (
          <ConvertibleNote cell={cell as ConvertibleNotesLedgerCell} closeDialog={() => {}} isDisabled={isDisabled} />
        ),
        maxWidth: 'lg',
      };
      cell.minValue = null;
      cell.disableEvents = true;
      cell.tooltipMessages = tooltipMessages;
      cell.dbType = 'string';
      cell.readOnly = isDisabled;
      cell.disableWhenLedgerOpen = false;
    } else {
      setAsHidden();
      cell.tooltipMessages = tooltipMessages;
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 22.  Cash or PIK
  if (alias === CAPTABLE.DIVIDEND_PAYMENT_TYPE_ALIAS) {
    if (preferredTerms) {
      setAsEditable();
      if (hasDividends) {
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.DIVIDEND_PAYMENT_TYPE} {...props} />;
        cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
        setAsRequired();
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
        cell.tooltipMessages = tooltipMessages;
        cell.expr = defaultValue as number;
        cell.value = defaultValue as number;
      }
    } else {
      setAsHidden();
      cell.tooltipMessages = tooltipMessages;
      cell.expr = defaultValue as number;
      cell.value = defaultValue as number;
    }
  }

  // 23.  Dividend Rate
  if (alias === CAPTABLE.DIVIDEND_RATE_ALIAS) {
    if (preferredTerms) {
      if (hasDividends) {
        setAsEditable();
        // when had multiple div rates or variable liq pref and deactivated through the variable chip
        if (
          cell.value === VARIABLE_VALUE &&
          !tableData.has_dividend_rate_ledger &&
          !tableData.has_liquidation_preference_ledger
        ) {
          cell.value = cell.defaultValue as CellValue;
        }
      } else {
        setAsHidden();
      }
    } else {
      setAsHidden();
    }
  }

  // 24.  Compounding
  if (alias === CAPTABLE.COMPOUNDING_ALIAS) {
    if (preferredTerms) {
      if (hasDividends) {
        setAsEditable();
        setAsRequired();
        cell.dataEditor = (props: any) => <GridSelect options={inputOptions.YES_NO} {...props} />;
        cell.valueViewer = (props: any) => <SelectValueViewer {...props} />;
        cell.skipInitialValidation = true;
      } else {
        setAsHidden();
      }
    } else {
      setAsHidden();
    }
  }

  // 25.  Compounding Periods/Year
  if (alias === CAPTABLE.COMPOUNDING_PERIOD_ALIAS) {
    if (preferredTerms) {
      if (hasDividends) {
        const hasCompounding = capCells[columnLegend! + CAPTABLE.COMPOUNDING_ROW_NUMBER];

        if (hasCompounding && Number(hasCompounding.value) === inputOptions.YES && !hasDivRateLedgerItems) {
          setAsEditable();
        } else if (hasCompounding && Number(hasCompounding.value) && hasDivRateLedgerItems) {
          cell.readOnly = true;
        } else {
          setAsHidden();
          cell.expr = '1';
          cell.value = '1';
        }
      } else {
        setAsHidden();
        cell.expr = '0';
        cell.value = '0';
      }
    } else {
      setAsHidden();
      cell.expr = '0';
      cell.value = '0';
    }
  }

  // 26.  Accrued Dividends (as of 12/31/2018)
  if (alias === CAPTABLE.ACCRUED_DIVIDENDS_ALIAS) {
    setAsHidden(true);
    if (preferredTerms) {
      const cumulativeDividendsKey = columnLegend! + CAPTABLE.CUMULATIVE_DIVIDENDS_ROW_NUMBER;
      const cumulativeDividendsCell = capCells[cumulativeDividendsKey];
      const cumulativeDividendsValue = cumulativeDividendsCell?.value;
      const hasCumulativeDividends = toString(cumulativeDividendsValue as string) === '1';

      if (hasCumulativeDividends) {
        if (hasMultipleInvestments) {
          setAsReadOnly();
          cell.dialog = {
            content: <MultipleInvestmentDates cell={cell as MultipleInvestmentDatesCell} closeDialog={() => {}} />,
          };
          cell.disableEvents = true;
        } else {
          setAsReadOnly();
          cell.dialog = null;
          cell.className = removeClassName(className ?? '', 'read-only--white');
        }
      }
    }
  }

  // 27.  Initial Liquidation Preference
  if (alias === CAPTABLE.INITIAL_LIQUIDATION_PREFERENCE_ALIAS) {
    if (preferredTerms) {
      setAsReadOnly();
    } else {
      setAsHidden(true);
    }
  }

  // 28.  Total Preference
  if (alias === CAPTABLE.TOTAL_PREFERENCE_ALIAS) {
    if (preferredTerms) {
      cell.hidden = false;
    } else {
      setAsHidden(true);
    }
  }

  if (
    alias === CAPTABLE.LIQUIDATION_PREFERENCE_ALIAS &&
    tableData.has_liquidation_preference_ledger &&
    isSecurityType(PREFERRED) &&
    !hasMultipleInvestments
  ) {
    cell.dialog = {
      content: (
        <LiquidationPreferenceLedgerDialog
          cell={cell as ICell}
          closeDialog={() => {}}
          isDisabled={!!isDisabled}
          onInit={() => {
            cell.readOnly = true;
          }}
          tableData={tableData as any}
        />
      ),
    };
    cell.hidden = false;
    // if there is a ledger disable the liquidation preference input as the ledger dictates the value
    const ledgerExists = !!capCells[columnLegend! + CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER]?.value;

    cell.readOnly = ledgerExists;
  }

  // if the ledger is enabled then disable the cells that are influenced by the ledger
  if (
    alias === CAPTABLE.LIQUIDATION_PREFERENCE_LEDGER_ALIAS &&
    tableData.has_liquidation_preference_ledger &&
    isSecurityType(PREFERRED) &&
    !hasMultipleInvestments
  ) {
    const ledgerValue = value as LiqPrefLedgerConfig;
    if (!ledgerValue?.threshold_type) {
      return cell;
    }

    const ledgerInfluencedCells = CAPTABLE.liqPrefLedgerInfluencedCellAliases.map(
      ({ rowNumber }) => capCells[columnLegend! + rowNumber]
    );

    // disable eslint for this line as we want to mutate the cells in memory
    /* eslint-disable no-return-assign */
    ledgerInfluencedCells.forEach(ledgerInfluencedCell => {
      const ledgerValuesForAlias = ledgerValue.items?.map(
        (ledgerItem: LiqPrefLedgerItem) => ledgerItem[ledgerInfluencedCell.alias as keyof LiqPrefLedgerItem]
      );
      const uniqueLedgerValuesForAlias = new Set(ledgerValuesForAlias);
      const allLedgerValuesAreTheSame = new Set(uniqueLedgerValuesForAlias).size === 1;

      // the tooltip is only shown if the values are not the same and after the values are filtered for null values
      ledgerInfluencedCell.readOnly = tableData.has_liquidation_preference_ledger;
      ledgerInfluencedCell.defaultTooltip = allLedgerValuesAreTheSame
        ? ''
        : uniqueLedgerValuesForAlias
          .values()
          .toArray()
          .filter(i => i !== null)
          .map((i: any) => +i)
          .join(', ');

      // for boolean dbTypes continue with default value viewer
      if (!allLedgerValuesAreTheSame && ledgerInfluencedCell.dbType !== 'boolean') {
        ledgerInfluencedCell.valueViewer = (props: any) => <VariableValueViewer {...props} />;
      }

      // need to set the liq pref ledger cell to readonly if the ledger is open
      ledgerInfluencedCell.dialog = {
        content: (
          <LiquidationPreferenceLedgerDialog
            cell={cell as ICell}
            isDisabled={!!isDisabled}
            closeDialog={() => {}}
            tableData={tableData as any}
          />
        ),
      };
    });
  }

  if (
    alias === CAPTABLE.DIVIDEND_RATE_ALIAS &&
    tableData.has_dividend_rate_ledger &&
    isSecurityType(PREFERRED) &&
    hasDividends &&
    !hasMultipleInvestments
  ) {
    cell.dialog = {
      content: (
        <DividendRateLedger
          cell={cell as ICell}
          closeDialog={() => {}}
          tableData={tableData as any}
          onInit={() => {
            cell.readOnly = true;
          }}
        />
      ),
    };
    cell.hidden = false;
    cell.readOnly = hasDivRateLedgerItems;
    if (cell.value === VARIABLE_VALUE) {
      cell.gridType = STRING;
      cell.format = undefined;
    } else {
      cell.gridType = PERCENTAGE;
      cell.format = capTableTwoDecimalPercentFormat;
    }
  }

  // Green means the cell has an active Excel reference. Set after the per-field blocks, which
  // rebuild the class from its pre-pass value, so none of them can drop or revive it.
  syncReferenceGreen(cell);

  // THREE-WAY DISPLAY COMPOSITION (SCAL-8984): a cell can carry all three
  // mapping-ish types at once - MappingReference, CitationReference and an AI
  // field_metadata entry - and each renders its own ADDITIVE indicator; none
  // suppresses another:
  //   - MappingReference  -> green 'cell-with-references' class while one is active (syncReferenceGreen, above);
  //   - CitationReference -> CitationValueViewer wrap (INNER, here): one footnote
  //     badge per distinct document, dismissed rows filtered out in the viewer;
  //   - Applicable AI field_metadata -> AIMerge/AIFindingValueViewer wrap (OUTER, next block).
  // Rebuild the review composition from its true base on every pass. Conditions
  // runs repeatedly on the same mutable cells, while citations and AI metadata
  // can arrive or clear independently; retaining prior wrappers would therefore
  // accumulate duplicates or leave stale review UI behind.
  const reviewBase = reviewBaseOf(cell.valueViewer);
  const valueReviewApplicable = isCapTableFieldApplicable(cell, capCells, 'value', tableData);
  let composedViewer = reviewBase;
  cell.className = removeClassName(cell.className ?? '', AI_SUGGESTED_CLASS);

  if (cell.citationReferences && cell.citationReferences.length > 0) {
    const originalValueViewer = composedViewer;
    composedViewer = (props: any) => (
      <CitationValueViewer
        value={props.value}
        citationReferences={cell.citationReferences}
        originalValueViewer={originalValueViewer}
        {...props}
        valueReviewApplicable={valueReviewApplicable}
      />
    );
  }

  if (cell.aiFieldMetadata && valueReviewApplicable) {
    const originalValueViewer = composedViewer;
    const fieldLabel = cell.alias ? cell.alias.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Field';

    const aiVal = cell.aiFieldMetadata.value;
    const currentVal = cell.value;
    const hasCurrentValue = currentVal !== null && currentVal !== undefined && currentVal !== '';
    // Select-backed cells (cell.options - e.g. Underlying Security) store the
    // option ID while the AI suggests the option NAME: compare in RESOLVED
    // space, or a suggestion matching the current selection always "differs"
    // (name vs ref UUID) and renders as a merge instead of a finding. Falls
    // back to the raw compare when nothing resolves. Numeric values compare
    // in DOUBLE space (sameAiValue): two decimal spellings of one double
    // (shortest repr vs the column's 15dp expansion) are the same value, so
    // an echo suggestion renders as a plain finding, never a merge arrow.
    const comparableAiVal = normalizeAiSelectValue(cell.alias, aiVal) ?? aiVal;
    const resolvedAiVal =
      matchAiOption((cell as { options?: { id?: unknown; name?: unknown }[] }).options, comparableAiVal)?.id ??
      comparableAiVal;
    const hasMergeDiff = aiVal !== undefined && hasCurrentValue && !sameAiValue(resolvedAiVal, currentVal);

    composedViewer = hasMergeDiff
      ? (props: any) => (
        <AIMergeValueViewer
          value={props.value}
          aiFieldMetadata={cell.aiFieldMetadata!}
          aiAccuracyConfidence={cell.aiAccuracyConfidence}
          fieldLabel={fieldLabel}
          securityId={cell.columnId}
          fieldAlias={cell.alias}
          originalValueViewer={originalValueViewer}
          {...props}
        />
      )
      : (props: any) => (
        <AIFindingValueViewer
          value={props.value}
          aiFieldMetadata={cell.aiFieldMetadata!}
          aiAccuracyConfidence={cell.aiAccuracyConfidence}
          fieldLabel={fieldLabel}
          securityId={cell.columnId}
          fieldAlias={cell.alias}
          originalValueViewer={originalValueViewer}
          {...props}
        />
      );

    // The class is visually inert but advertises AI provenance in the DOM; like
    // the rest of the review chrome it is analyst-side only (SCAL-9335).
    if (!cell.isAiGeneratedColumn && useReviewStore.getState().reviewerMode) {
      cell.className = addClassName(cell.className ?? '', AI_SUGGESTED_CLASS);
    }
  }

  if (composedViewer !== reviewBase) {
    (composedViewer as typeof composedViewer & { reviewBase?: typeof reviewBase }).reviewBase = reviewBase;
  }
  cell.valueViewer = composedViewer;

  // SCAL-9155: the dashed AI-generated-column border is part of the retired in-grid
  // AI column UI - apply it only when the (default-OFF) feature toggle is on.
  if (cell.isAiGeneratedColumn && useReviewStore.getState().showAiColumnControls) {
    cell.className = addClassName(cell.className ?? '', 'cell-ai-generated-column');
  }

  return cell;
};

export default conditions;
